import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import AppLayout from '../components/AppLayout';
import RoleGate from '../components/RoleGate';
import VideoThumbnail from '../components/VideoThumbnail';
import { ROLES } from '../lib/auth/roles';
import { Icons } from '../lib/Icons';
import { getActivityBySlug, saveActivityResource } from '../api/resources';
import { bulkCheckResources, updateResourceReview, deleteResource, calculateAppropriatenessScore, searchVideosForActivity, acceptVideoCandidate, calculateVideoScore } from '../api/adminResources';
import { fetchTranscript, isExtensionAvailable, getInstallationInstructions } from '../utils/ExtensionBridge';
import { useUserRole } from '../lib/auth/roles';

// Helper function to normalize legacy resource formats to array
const normalizeResources = (rawResources, activityId = 'activity') => {
  if (!rawResources) return [];
  
  if (Array.isArray(rawResources)) {
    return rawResources;
  }
  
  if (typeof rawResources === 'object') {
    const resources = [];
    const legacy = rawResources;
    const videos = legacy.video || legacy.videos || [];
    const buy = legacy.buy || [];
    const diy = legacy.diy || [];
    const docs = legacy.doc || legacy.docs || [];
    
    videos.forEach((v, i) => resources.push({
      id: v.id || `${activityId}-legacy-video-${i}`,
      kind: 'video',
      title: v.title || v.name || 'Video',
      source: 'youtube',
      baseUrl: v.url || v.baseUrl || '',
      affiliate: v.affiliate || { enabled: false },
      redirectId: v.redirectId || `${activityId}-redirect-video-${i}`,
      status: v.status || { isActive: true },
      meta: v.meta || {},
    }));
    
    buy.forEach((b, i) => resources.push({
      id: b.id || `${activityId}-legacy-buy-${i}`,
      kind: 'buy',
      title: b.title || b.name || 'Buy',
      source: 'amazon',
      baseUrl: b.url || b.baseUrl || '',
      affiliate: b.affiliate || { enabled: false },
      redirectId: b.redirectId || `${activityId}-redirect-buy-${i}`,
      status: b.status || { isActive: true },
      meta: b.meta || {},
    }));
    
    diy.forEach((d, i) => resources.push({
      id: d.id || `${activityId}-legacy-diy-${i}`,
      kind: 'diy',
      title: d.title || d.name || 'DIY',
      source: 'other',
      baseUrl: d.url || d.baseUrl || '',
      affiliate: { enabled: false },
      redirectId: d.redirectId || `${activityId}-redirect-diy-${i}`,
      status: d.status || { isActive: true },
      meta: d.meta || {},
    }));
    
    docs.forEach((d, i) => resources.push({
      id: d.id || `${activityId}-legacy-doc-${i}`,
      kind: 'doc',
      title: d.title || d.name || 'Document',
      source: 'other',
      baseUrl: d.url || d.baseUrl || '',
      affiliate: { enabled: false },
      redirectId: d.redirectId || `${activityId}-redirect-doc-${i}`,
      status: d.status || { isActive: true },
      meta: d.meta || {},
    }));
    
    return resources;
  }
  
  return [];
};

const AdminActivityEditor = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { role } = useUserRole();
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);
  const [showOnlyUnavailable, setShowOnlyUnavailable] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [showBulkAddModal, setShowBulkAddModal] = useState(false);
  const [reviewingResource, setReviewingResource] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showVideoSearchModal, setShowVideoSearchModal] = useState(false);

  useEffect(() => {
    loadActivity();
  }, [slug]);

  const loadActivity = async () => {
    try {
      setLoading(true);
      const data = await getActivityBySlug(slug);
      setActivity(data);
    } catch (error) {
      console.error('Error loading activity:', error);
      toast.error('Failed to load activity');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveResource = async (resourceData, resourceIndex) => {
    try {
      setSaving(true);
      await saveActivityResource(slug, resourceData, resourceIndex);
      toast.success('Resource saved successfully');
      setShowResourceModal(false);
      setEditingResource(null);
      await loadActivity();
    } catch (error) {
      console.error('Error saving resource:', error);
      if (error.message === 'RESOURCE_URL_NOT_UNIQUE') {
        toast.error('This URL is already used by another activity');
      } else {
        toast.error('Failed to save resource');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteResource = async (index) => {
    if (!window.confirm('Are you sure you want to delete this resource?')) {
      return;
    }

    try {
      setSaving(true);
      
      // Use the Cloud Function to delete the resource (handles permissions)
      await deleteResource(activity.id, index);
      
      toast.success('Resource deleted');
      await loadActivity();
    } catch (error) {
      console.error('Error deleting resource:', error);
      toast.error('Failed to delete resource');
    } finally {
      setSaving(false);
    }
  };

  const handleManualCheck = async () => {
    if (!window.confirm('This will check all resources for this activity. Continue?')) {
      return;
    }

    try {
      setChecking(true);
      await bulkCheckResources();
      toast.success('Resource check started. Refreshing...');
      setTimeout(() => {
        loadActivity();
      }, 3000);
    } catch (error) {
      console.error('Error starting manual check:', error);
      toast.error('Failed to start resource check');
    } finally {
      setChecking(false);
    }
  };

  const handleCalculateScore = async (resource) => {
    try {
      const result = await calculateAppropriatenessScore({
        activitySlug: slug,
        resourceId: resource.id,
      });
      toast.success(`Appropriateness score: ${result.score}/100`);
      await loadActivity();
    } catch (error) {
      console.error('Error calculating score:', error);
      toast.error('Failed to calculate appropriateness score');
    }
  };

  const handleSaveReview = async (reviewData) => {
    try {
      setSaving(true);
      await updateResourceReview({
        activitySlug: slug,
        resourceId: reviewingResource.id,
        ...reviewData,
      });
      toast.success('Review saved successfully');
      setShowReviewModal(false);
      setReviewingResource(null);
      await loadActivity();
    } catch (error) {
      console.error('Error saving review:', error);
      toast.error('Failed to save review');
    } finally {
      setSaving(false);
    }
  };

  // Get normalized resources array
  const getNormalizedResources = () => {
    return normalizeResources(activity?.resources, activity?.id || slug);
  };

  const getFilteredResources = () => {
    const resources = getNormalizedResources();
    if (showOnlyUnavailable) {
      return resources.filter(r => r.status?.lastOk === false);
    }
    return resources;
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-lg">Loading...</div>
        </div>
      </AppLayout>
    );
  }

  if (!activity) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-lg text-red-600">Activity not found</div>
        </div>
      </AppLayout>
    );
  }

  const filteredResources = getFilteredResources();

  return (
    <RoleGate allow={[ROLES.SENCO, ROLES.SEN_CO, ROLES.SUPER_ADMIN]} fallback={<div>Access denied. SENCO or super admin role required.</div>}>
      <AppLayout>
        <div className="container mx-auto px-4 py-8 max-w-6xl">
          <div className="mb-6">
            <button
              onClick={() => navigate('/admin/resources')}
              className="text-blue-600 hover:text-blue-800 mb-4"
            >
              ← Back to Resource Management
            </button>
            <h1 className="text-3xl font-bold mb-2">{activity.title || activity.slug}</h1>
            <p className="text-gray-600">Edit activity resources</p>
          </div>

          {/* Resources Table */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Resources</h2>
              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={showOnlyUnavailable}
                    onChange={(e) => setShowOnlyUnavailable(e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm">Show only unavailable</span>
                </label>
                {role === ROLES.SUPER_ADMIN && (
                  <button
                    onClick={handleManualCheck}
                    disabled={checking}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                  >
                    {checking ? 'Checking...' : 'Check Now'}
                  </button>
                )}
                <button
                  onClick={() => {
                    setEditingResource(null);
                    setShowResourceModal(true);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  + Add Resource
                </button>
                {role === ROLES.SUPER_ADMIN && (
                  <>
                    <button
                      onClick={() => setShowVideoSearchModal(true)}
                      className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 inline-flex items-center gap-2"
                    >
                      <Icons.MagnifyingGlass className="w-4 h-4" /> Search YouTube
                    </button>
                    <button
                      onClick={() => setShowBulkAddModal(true)}
                      className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
                    >
                      Bulk Add
                    </button>
                  </>
                )}
              </div>
            </div>

            {filteredResources.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No resources found
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Kind
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Title
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Source
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        URL
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredResources.map((resource, index) => {
                      const allResources = getNormalizedResources();
                      const actualIndex = allResources.findIndex(r => r.id === resource.id);
                      const isUnavailable = resource.status?.lastOk === false;
                      
                      return (
                        <tr key={resource.id || index} className={isUnavailable ? 'bg-red-50' : ''}>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                            {resource.kind}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">{resource.title}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            {resource.source}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 truncate max-w-xs">
                            <a
                              href={resource.baseUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline"
                            >
                              {resource.baseUrl}
                            </a>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex flex-col gap-1">
                              {isUnavailable ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  Unavailable
                                </span>
                              ) : resource.status?.lastOk === true ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  OK
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                  Unknown
                                </span>
                              )}
                              {resource.status?.needsReview && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  Needs Review
                                </span>
                              )}
                              {resource.status?.appropriatenessScore !== null && resource.status?.appropriatenessScore !== undefined && (
                                <span className="text-xs text-gray-500">
                                  Score: {resource.status.appropriatenessScore}/100
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                            <div className="flex gap-2">
                              <button
                                onClick={() => {
                                  setEditingResource({ ...resource, index: actualIndex });
                                  setShowResourceModal(true);
                                }}
                                className="text-blue-600 hover:text-blue-900"
                              >
                                Edit
                              </button>
                              {role === ROLES.SUPER_ADMIN && resource.kind === 'video' && resource.source === 'youtube' && (
                                <>
                                  <button
                                    onClick={() => handleCalculateScore(resource)}
                                    className="text-purple-600 hover:text-purple-900"
                                    title="Calculate appropriateness score"
                                  >
                                    Score
                                  </button>
                                  <button
                                    onClick={() => {
                                      setReviewingResource(resource);
                                      setShowReviewModal(true);
                                    }}
                                    className="text-green-600 hover:text-green-900"
                                    title="Review resource"
                                  >
                                    Review
                                  </button>
                                </>
                              )}
                              <button
                                onClick={() => handleDeleteResource(actualIndex)}
                                className="text-red-600 hover:text-red-900"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Resource Edit Modal */}
          {showResourceModal && (
            <ResourceEditModal
              resource={editingResource}
              onSave={handleSaveResource}
              onClose={() => {
                setShowResourceModal(false);
                setEditingResource(null);
              }}
              saving={saving}
            />
          )}

          {/* Bulk Add Modal */}
          {showBulkAddModal && role === ROLES.SUPER_ADMIN && (
            <BulkAddModal
              activitySlug={slug}
              onClose={() => setShowBulkAddModal(false)}
              onSave={async () => {
                setShowBulkAddModal(false);
                await loadActivity();
              }}
            />
          )}

          {/* Review Modal */}
          {showReviewModal && reviewingResource && (
            <ReviewModal
              resource={reviewingResource}
              onSave={handleSaveReview}
              onClose={() => {
                setShowReviewModal(false);
                setReviewingResource(null);
              }}
              saving={saving}
            />
          )}

          {/* Video Search Modal */}
          {showVideoSearchModal && role === ROLES.SUPER_ADMIN && (
            <VideoSearchModal
              activitySlug={activity.slug || activity.id || slug}
              activityTitle={activity.title || activity.slug || slug}
              onClose={() => setShowVideoSearchModal(false)}
              onAccept={async () => {
                setShowVideoSearchModal(false);
                await loadActivity();
              }}
            />
          )}
        </div>
      </AppLayout>
    </RoleGate>
  );
};

const ResourceEditModal = ({ resource, onSave, onClose, saving }) => {
  const [formData, setFormData] = useState({
    kind: 'video',
    title: '',
    source: 'youtube',
    baseUrl: '',
    affiliate: {
      enabled: false,
      provider: null,
      params: {},
    },
    status: {
      isActive: true,
    },
  });

  useEffect(() => {
    if (resource) {
      setFormData({
        kind: resource.kind || 'video',
        title: resource.title || '',
        source: resource.source || 'youtube',
        baseUrl: resource.baseUrl || '',
        affiliate: {
          enabled: resource.affiliate?.enabled || false,
          provider: resource.affiliate?.provider || null,
          params: resource.affiliate?.params || {},
        },
        status: {
          isActive: resource.status?.isActive !== undefined ? resource.status.isActive : true,
        },
        id: resource.id,
        redirectId: resource.redirectId,
      });
    }
  }, [resource]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData, resource?.index);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-4">
          {resource ? 'Edit Resource' : 'Add Resource'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Kind *
            </label>
            <select
              value={formData.kind}
              onChange={(e) => setFormData({ ...formData, kind: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              required
            >
              <option value="video">Video</option>
              <option value="buy">Buy</option>
              <option value="diy">DIY</option>
              <option value="doc">Document</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Source *
            </label>
            <select
              value={formData.source}
              onChange={(e) => setFormData({ ...formData, source: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              required
            >
              <option value="youtube">YouTube</option>
              <option value="vimeo">Vimeo</option>
              <option value="amazon">Amazon</option>
              <option value="etsy">Etsy</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Base URL *
            </label>
            <input
              type="url"
              value={formData.baseUrl}
              onChange={(e) => setFormData({ ...formData, baseUrl: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              required
            />
          </div>

          <div className="border-t pt-4">
            <h3 className="text-lg font-semibold mb-3">Affiliate Settings</h3>
            
            <label className="flex items-center gap-2 mb-3">
              <input
                type="checkbox"
                checked={formData.affiliate.enabled}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    affiliate: { ...formData.affiliate, enabled: e.target.checked },
                  })
                }
                className="rounded"
              />
              <span>Enable affiliate tracking</span>
            </label>

            {formData.affiliate.enabled && (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Provider
                  </label>
                  <select
                    value={formData.affiliate.provider || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        affiliate: { ...formData.affiliate, provider: e.target.value || null },
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="">None</option>
                    <option value="amazon_uk">Amazon UK</option>
                    <option value="awin">Awin</option>
                    <option value="impact">Impact</option>
                  </select>
                </div>

                {formData.affiliate.provider === 'amazon_uk' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Amazon Tag
                    </label>
                    <input
                      type="text"
                      value={formData.affiliate.params?.tag || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          affiliate: {
                            ...formData.affiliate,
                            params: { ...formData.affiliate.params, tag: e.target.value },
                          },
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="your-amazon-tag"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const BulkAddModal = ({ activitySlug, onClose, onSave }) => {
  const [urls, setUrls] = useState('');
  const [resources, setResources] = useState([]);
  const [saving, setSaving] = useState(false);

  const parseUrls = () => {
    const urlList = urls
      .split(/\n|,/)
      .map(url => url.trim())
      .filter(url => url.length > 0);

    const parsed = urlList.map((url, index) => {
      const isYouTube = url.includes('youtube.com') || url.includes('youtu.be');
      return {
        id: `bulk-${index}`,
        kind: isYouTube ? 'video' : 'doc',
        title: '',
        source: isYouTube ? 'youtube' : 'other',
        baseUrl: url,
        affiliate: { enabled: false },
        status: { isActive: true },
      };
    });

    setResources(parsed);
  };

  const updateResource = (index, updates) => {
    const updated = [...resources];
    updated[index] = { ...updated[index], ...updates };
    setResources(updated);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      for (const resource of resources) {
        if (!resource.title) {
          toast.error(`Please provide a title for: ${resource.baseUrl}`);
          return;
        }
        await saveActivityResource(activitySlug, resource);
      }
      toast.success(`Successfully added ${resources.length} resources`);
      onSave();
    } catch (error) {
      console.error('Error saving bulk resources:', error);
      toast.error('Failed to save resources');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-4">Bulk Add Resources</h2>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Paste URLs (one per line or comma-separated)
          </label>
          <textarea
            value={urls}
            onChange={(e) => setUrls(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md h-32"
            placeholder="https://youtube.com/watch?v=...&#10;https://example.com/resource"
          />
          <button
            onClick={parseUrls}
            className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Parse URLs
          </button>
        </div>

        {resources.length > 0 && (
          <div className="mb-4">
            <h3 className="text-lg font-semibold mb-2">Review Resources ({resources.length})</h3>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {resources.map((resource, index) => (
                <div key={resource.id} className="border rounded p-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-700">Title</label>
                      <input
                        type="text"
                        value={resource.title}
                        onChange={(e) => updateResource(index, { title: e.target.value })}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                        placeholder="Resource title"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700">Kind</label>
                      <select
                        value={resource.kind}
                        onChange={(e) => updateResource(index, { kind: e.target.value })}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      >
                        <option value="video">Video</option>
                        <option value="buy">Buy</option>
                        <option value="diy">DIY</option>
                        <option value="doc">Document</option>
                      </select>
                    </div>
                  </div>
                  <div className="mt-2">
                    <span className="text-xs text-gray-500">{resource.baseUrl}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
            disabled={saving}
          >
            Cancel
          </button>
          {resources.length > 0 && (
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              disabled={saving}
            >
              {saving ? 'Saving...' : `Save ${resources.length} Resources`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const ReviewModal = ({ resource, onSave, onClose, saving }) => {
  const [formData, setFormData] = useState({
    appropriatenessFlag: resource.status?.appropriatenessFlag || null,
    reviewNotes: resource.status?.reviewNotes || '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full">
        <h2 className="text-2xl font-bold mb-4">Review Resource</h2>

        <div className="mb-4">
          <div className="font-semibold">{resource.title}</div>
          <div className="text-sm text-gray-500">{resource.baseUrl}</div>
          {resource.status?.appropriatenessScore !== null && resource.status?.appropriatenessScore !== undefined && (
            <div className="mt-2">
              <span className="text-sm">Appropriateness Score: </span>
              <span className={`font-semibold ${
                resource.status.appropriatenessScore >= 70 ? 'text-green-600' :
                resource.status.appropriatenessScore >= 40 ? 'text-yellow-600' :
                'text-red-600'
              }`}>
                {resource.status.appropriatenessScore}/100
              </span>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Appropriateness Flag
            </label>
            <select
              value={formData.appropriatenessFlag || ''}
              onChange={(e) => setFormData({ ...formData, appropriatenessFlag: e.target.value || null })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="">Not Reviewed</option>
              <option value="appropriate">Appropriate</option>
              <option value="inappropriate">Inappropriate</option>
              <option value="needs-review">Needs Review</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Review Notes
            </label>
            <textarea
              value={formData.reviewNotes}
              onChange={(e) => setFormData({ ...formData, reviewNotes: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md h-32"
              placeholder="Add notes about this resource..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Review'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const VideoSearchModal = ({ activitySlug, activityTitle, onClose, onAccept }) => {
  const [searchQuery, setSearchQuery] = useState(`${activityTitle} educational SEN tutorial`);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [fetchingTranscripts, setFetchingTranscripts] = useState(false);
  const [extensionAvailable, setExtensionAvailable] = useState(null);
  const [accepting, setAccepting] = useState(null);
  const [previewVideo, setPreviewVideo] = useState(null);

  // Check extension availability on mount
  useEffect(() => {
    console.log('VideoSearchModal mounted, checking extension...');
    isExtensionAvailable()
      .then((available) => {
        console.log('Extension available:', available);
        setExtensionAvailable(available);
        if (!available) {
          console.warn('Extension not detected. Make sure it is installed and enabled.');
        }
      })
      .catch((error) => {
        console.error('Error checking extension:', error);
        setExtensionAvailable(false);
      });
    
    // Also check if the global marker exists
    setTimeout(() => {
      if (window.__youtubeTranscriptHelperAvailable) {
        console.log('Extension detected via global marker');
        setExtensionAvailable(true);
      } else {
        console.log('Extension global marker not found');
      }
    }, 500);
  }, []);

  const handleSearch = async () => {
    try {
      setSearching(true);
      setResults([]);
      
      // Use server-side scoring with RapidAPI transcripts
      console.log('Starting server-side video search with transcript scoring...');
      toast.loading('Searching and scoring videos (fetching transcripts)...', { id: 'video-search' });
      
      const response = await searchVideosForActivity({
        activitySlug,
        customQuery: searchQuery,
        maxResults: 5,
        skipScoring: false, // Use server-side RapidAPI transcript fetching
      });
      
      toast.dismiss('video-search');
      
      const scoredResults = response.results || [];
      console.log('Scored video results:', scoredResults.length);
      
      // Log auto-research info if available
      if (response.autoResearch?.performed) {
        console.log(`Auto-research performed: ${response.autoResearch.rounds} rounds, ${response.autoResearch.totalResultsFound} total results`);
        toast.success(`Auto-research found ${response.autoResearch.totalResultsFound} videos across ${response.autoResearch.rounds} search rounds`);
      }
      
      // Log any auto-accepted videos
      if (response.autoAcceptedCount > 0) {
        console.log(`Auto-accepted ${response.autoAcceptedCount} videos:`, response.autoAccepted);
        toast.success(`${response.autoAcceptedCount} high-scoring video(s) auto-accepted!`);
      }
      
      // Results are already scored by server, just set them
      if (scoredResults.length > 0) {
        // Sort by score descending (should already be sorted, but ensure)
        scoredResults.sort((a, b) => (b.appropriatenessScore || 0) - (a.appropriatenessScore || 0));
        
        // Log transcript usage
        const withTranscript = scoredResults.filter(v => v.usedTranscript).length;
        console.log(`Videos with transcript: ${withTranscript}/${scoredResults.length}`);
        
        scoredResults.forEach(video => {
          console.log(`${video.title}: score=${video.appropriatenessScore}, transcript=${video.usedTranscript ? 'yes' : 'no'}`);
        });
        
        // Set results directly (already scored)
        setResults(scoredResults);
        setFetchingTranscripts(false);
        setSearching(false);
        
        toast.success(`Found ${scoredResults.length} video${scoredResults.length !== 1 ? 's' : ''}`);
        return; // Exit early - we're done
      }
      
      // No results found
      setResults([]);
      toast.error('No videos found. Try a different search query.');
      
    } catch (error) {
      console.error('Error searching videos:', error);
      toast.dismiss('video-search');
      toast.error('Failed to search videos: ' + (error.message || 'Unknown error'));
    } finally {
      setSearching(false);
      setFetchingTranscripts(false);
    }
  };

  const handleAccept = async (candidate) => {
    try {
      setAccepting(candidate.videoId);
      await acceptVideoCandidate({
        activitySlug,
        candidate,
      });
      toast.success('Video added to activity');
      onAccept();
    } catch (error) {
      console.error('Error accepting video:', error);
      toast.error('Failed to add video');
    } finally {
      setAccepting(null);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 70) return 'bg-green-100 text-green-800';
    if (score >= 40) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold">Search YouTube Videos</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl"
            >
              ×
            </button>
          </div>

          <p className="text-gray-600 mb-4">
            Search for videos related to: <strong>{activityTitle}</strong>
          </p>

          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-800">
              <strong>Enhanced scoring enabled:</strong> Videos will be scored using transcripts (when available) and metadata analysis.
              High-scoring videos (70%+) may be auto-accepted.
            </p>
          </div>

          <div className="flex gap-2 mb-6">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
              placeholder="Enter search query..."
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <button
              onClick={handleSearch}
              disabled={searching || fetchingTranscripts}
              className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
            >
              {fetchingTranscripts ? 'Scoring...' : searching ? 'Searching...' : 'Search'}
            </button>
          </div>

          {results.length > 0 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">
                Results ({results.length} videos found)
              </h3>
              
              {results.map((video) => (
                <div
                  key={video.videoId}
                  className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex gap-4">
                    <div className="relative">
                      <VideoThumbnail
                        src={video.thumbnail}
                        videoId={video.videoId}
                        alt={video.title}
                        className="w-40 h-24 object-cover rounded cursor-pointer"
                      />
                      <div 
                        className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 rounded cursor-pointer"
                        onClick={() => setPreviewVideo(video)}
                      >
                        <span className="text-white text-3xl">▶</span>
                      </div>
                    </div>
                    
                    <div className="flex-1">
                      <h4 className="font-semibold text-lg line-clamp-2">{video.title}</h4>
                      <p className="text-sm text-gray-600 mb-1">{video.channel}</p>
                      <p className="text-xs text-gray-500 line-clamp-2 mb-2">
                        {video.description}
                      </p>
                      
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getScoreColor(video.appropriatenessScore)}`}>
                          Score: {video.appropriatenessScore}/100
                        </span>
                        {video.llmEvaluation?.isRelevant === true && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            ✓ Relevant
                          </span>
                        )}
                        {video.llmEvaluation?.isRelevant === false && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            ✗ Not Relevant
                          </span>
                        )}
                        {video.usedTranscript && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            ✓ Transcript
                          </span>
                        )}
                        {video.transcriptError && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600" title={video.transcriptError}>
                            ⚠ No transcript
                          </span>
                        )}
                        <a
                          href={video.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 text-sm hover:underline"
                        >
                          Open in YouTube ↗
                        </a>
                      </div>
                      {video.llmEvaluation?.explanation && (
                        <p className="text-xs text-gray-600 mt-2 italic">
                          {video.llmEvaluation.explanation}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex flex-col gap-2">
                      <button
                        onClick={() => setPreviewVideo(video)}
                        className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                      >
                        Preview
                      </button>
                      <button
                        onClick={() => handleAccept(video)}
                        disabled={accepting === video.videoId}
                        className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                      >
                        {accepting === video.videoId ? 'Adding...' : 'Add to Activity'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!searching && results.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <p>Enter a search query and click "Search" to find videos</p>
            </div>
          )}
        </div>
      </div>

      {/* Video Preview Modal */}
      {previewVideo && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-60"
          onClick={() => setPreviewVideo(null)}
        >
          <div 
            className="bg-black rounded-lg overflow-hidden max-w-4xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="aspect-video">
              <iframe
                width="100%"
                height="100%"
                src={`https://www.youtube.com/embed/${previewVideo.videoId}?autoplay=1`}
                title={previewVideo.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <div className="p-4 bg-gray-900 text-white">
              <h3 className="font-semibold">{previewVideo.title}</h3>
              <p className="text-sm text-gray-400">{previewVideo.channel}</p>
              <div className="flex gap-3 mt-3">
                <button
                  onClick={() => setPreviewVideo(null)}
                  className="px-4 py-2 border border-gray-600 rounded hover:bg-gray-800"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    handleAccept(previewVideo);
                    setPreviewVideo(null);
                  }}
                  disabled={accepting === previewVideo.videoId}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                >
                  Add to Activity
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminActivityEditor;


